import React from "react";
import { Form, Button, Divider, Typography } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import TextInput from "../ReusableInputs/TextInput";
import { Link } from "react-router-dom";
import "./style.css";

const { Title, Text } = Typography;

// Loading component
const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#75A7AD]"></div>
  </div>
);

interface SigninFormProps {
  email: string;
  password: string;
  loading: boolean;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  onFinish: () => void;
  handleResendVerification: Function;
  isVerified: boolean;
}

const SigninForm: React.FC<SigninFormProps> = ({
  email,
  setEmail,
  password,
  setPassword,
  onFinish,
  loading,
  isVerified,
  handleResendVerification
}) => {
  const [form] = Form.useForm();

  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error("Please input your password"));
    }
    if (value.length < 8) {
      return Promise.reject(
        new Error("Password must be at least 8 characters long")
      );
    }
    if (!/[A-Z]/.test(value)) {
      return Promise.reject(
        new Error("Password must contain at least one uppercase letter")
      );
    }
    if (!/[\W_]/.test(value)) {
      return Promise.reject(
        new Error("Password must contain at least one special character")
      );
    }
    if (value.includes(" ")) {
      return Promise.reject(new Error("Password cannot contain spaces"));
    }
    return Promise.resolve();
  };

  return (
    <div className="relative w-full mt-[-39%] mb-[0px] lg:mb-[10px] p-4  opacity-80">
       <section className="flex items-center justify-center w-full py-10">
      <div className="bg-white rounded-xl shadow-lg border border-[#75A7AD]/20 w-full  sm:max-w-[55%] p-8">
        <div className="text-center mb-6">
          <Title level={2} className="text-[#005D6E] m-0">Welcome Back</Title>
          <Text className="text-gray-500">Sign in to your account</Text>
        </div>

        <Form form={form} onFinish={onFinish} layout="vertical">
          <TextInput
            type="email"
            title="Email Address"
            placeholder="Enter your email"
            inputValue={email}
            setInputValue={setEmail}
            name="email"
            required={true}
            prefix={<MailOutlined className="text-[#75A7AD] mr-2" />}
            tooltipText="Enter the email you used to register"
            rules={[
              {
                required: true,
                message: "Please input your email",
              },
              {
                type: "email",
                message: "Please enter a valid email address",
              },
            ]}
          />

          <TextInput
            type="password"
            title="Password"
            placeholder="Enter your password"
            inputValue={password}
            setInputValue={setPassword}
            name="password"
            required={true}
            prefix={<LockOutlined className="text-[#75A7AD] mr-2" />}
            rules={[{ validator: validatePassword }]}
          />

          <div className="flex justify-end mb-4">
            <Link to="/resquest-password-reset" className="text-[#005D6E] text-sm hover:text-[#75A7AD] transition-colors duration-300">
              Forgot Password?
            </Link>
          </div>

          <div className="mt-2">
            <Button
              htmlType="submit"
              className="custom-button w-full h-12 text-white font-medium rounded-full bg-gradient-to-r from-[#75A7AD] to-[#005D6E] hover:opacity-90 transition-all duration-300 flex items-center justify-center"
              disabled={loading}
            >
              {loading ? <Loader /> : "Sign In"}
            </Button>
          </div>

          {!isVerified && (
            <div className="mt-4">
              <Button
                onClick={() => handleResendVerification(email)}
                className="custom-button w-full h-12 text-white font-medium rounded-full bg-[#FF8200] hover:bg-[#FF8200]/90 transition-all duration-300 flex items-center justify-center"
              >
                Resend Verification Email
              </Button>
            </div>
          )}

          <Divider plain>
            <span className="text-gray-400 text-sm">or</span>
          </Divider>

          <div className="text-center mt-4">
            <Link to="/signup">
              <Text className="text-[#005D6E] hover:text-[#75A7AD] transition-colors duration-300">
                Don't have an account? <span className="font-semibold">Sign up</span>
              </Text>
            </Link>
          </div>
        </Form>
      </div>
    </section>
   </div>



  );
};

export default SigninForm;